import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getCalendarCategories, createCalendarCategory, updateCalendarCategory, deleteCalendarCategory } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const CalendarCategoryManagement = () => {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6366f1');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const load = async () => {
    try { const res = await getCalendarCategories(); setCategories(res.data || []); } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createCalendarCategory({ name: newName.trim(), color: newColor });
      setNewName(''); setNewColor('#6366f1');
      toast.success('Catégorie créée'); load();
    } catch { toast.error('Erreur création'); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateCalendarCategory(id, { name: editName.trim(), color: editColor });
      setEditingId(null); toast.success('Catégorie modifiée'); load();
    } catch { toast.error('Erreur modification'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try { await deleteCalendarCategory(id); toast.success('Catégorie supprimée'); load(); } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Catégories du calendrier</h3>
          <p className="text-sm text-gray-500 mt-1">Couleurs et types d'événements affichés dans le calendrier.</p>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-5 p-4 bg-gray-50 rounded-lg">
            <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="Nouvelle catégorie..." className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <button onClick={handleCreate} disabled={!newName.trim()}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm disabled:opacity-50">
              <Plus size={14} /> Ajouter
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Chargement...</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Aucune catégorie.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">Couleur</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        {editingId === c.id ? (
                          <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                        ) : (
                          <div className="w-6 h-6 rounded-full" style={{ backgroundColor: c.color }} />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingId === c.id ? (
                          <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleUpdate(c.id)}
                            className="w-full border rounded-md px-2 py-1 text-sm" autoFocus />
                        ) : (
                          <span className="text-sm text-gray-700">{c.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === c.id ? (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleUpdate(c.id)} className="p-1.5 text-green-600 hover:bg-green-100 rounded"><Check size={14} /></button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded"><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => { setEditingId(c.id); setEditName(c.name); setEditColor(c.color); }} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"><Edit size={14} /></button>
                            <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded"><Trash2 size={14} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarCategoryManagement;
